// auth.guard.ts
import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { AccountService } from '../services/account.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(private accountService: AccountService, private router: Router) {}

  /**
   * Verifica si el usuario está autenticado, la integridad de la sesión y que no haya expirado
   * @returns true si el usuario está autenticado, la sesión es íntegra y no ha expirado, false y redirige al login si no lo está
   */
  canActivate(): boolean {
    const session = this.accountService.getSession();
    
    // Si no hay sesión, redirigir al login
    if (!session) {
      console.warn('[AuthGuard] No session found, redirecting to login');
      this.router.navigate(['/login']);
      return false;
    }

    // Verificar si el token JWT está expirado
    const token = this.accountService.getToken();
    if (token && this.isTokenExpired(token)) {
      console.warn('[AuthGuard] Token JWT expired, logging out and redirecting to login');
      this.accountService.logout();
      this.router.navigate(['/login']);
      return false;
    }

    // Verificar si la sesión ha expirado (3 horas)
    if (this.accountService.isSessionExpired()) {
      console.warn('[AuthGuard] Session expired (3 hours), logging out user');
      this.accountService.logout();
      this.router.navigate(['/login']);
      return false;
    }

    // Verificar integridad de la sesión
    if (!this.accountService.verifySessionIntegrity()) {
      console.warn('[AuthGuard] Session integrity check failed, logging out user');
      this.accountService.logout();
      this.router.navigate(['/login']);
      return false;
    }

    return true;
  }

  /**
   * Verifica si el token JWT está expirado
   * @param token - El token JWT a verificar
   * @returns true si el token está expirado, false si sigue vigente
   */
  private isTokenExpired(token: string): boolean {
    try {
      // Decodificar el payload del JWT (segunda parte)
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // El campo 'exp' en JWT está en segundos, Date.now() está en milisegundos
      if (payload.exp) {
        const expirationTime = payload.exp * 1000; // Convertir a milisegundos
        const currentTime = Date.now();
        
        // Agregar un margen de 30 segundos para evitar problemas de timing
        return currentTime >= (expirationTime - 30000);
      }
      
      // Si no hay campo 'exp', considerar el token como válido
      return false;
    } catch (error) {
      console.error('[AuthGuard] Error decoding JWT token:', error);
      // Si hay error al decodificar, considerar el token como inválido/expirado
      return true;
    }
  }
}
